import * as React from 'react'
import { useState, useEffect, useContext, useMemo } from 'react'
import { ResourcesRecordShape } from '../../api/api_data_types'
import { minutesToTime, separateTime } from '../helpers/time'
import { useUpdateResourcesRecordMutation } from '../../hooks/queries/resources_records'
import { TimeDeltaContext } from './TimeDeltaContext'
import { EditModeContext } from './EditModeContext'

type InlineEditFormProps = {
  record: ResourcesRecordShape
  onCancel: () => void
  onSave: () => void
}

function useClampedTime(initialMinutes: number, maxHours = 23, maxMinutes = 59) {
  const [hours, setHours] = useState(() => {
    const [h] = separateTime(initialMinutes)
    return h
  })
  const [minutes, setMinutes] = useState(() => {
    const [, m] = separateTime(initialMinutes)
    return m
  })

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
    if (!isNaN(value)) {
      setHours(Math.max(0, Math.min(maxHours, value)))
    }
  }

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
    if (!isNaN(value)) {
      setMinutes(Math.max(0, Math.min(maxMinutes, value)))
    }
  }

  const currentMinutes = useMemo(() => hours * 60 + minutes, [hours, minutes])

  return { hours, minutes, handleHoursChange, handleMinutesChange, currentMinutes }
}

export default function InlineEditForm({ record, onCancel: _onCancel, onSave }: InlineEditFormProps) {
  const timeDeltaContext = useContext(TimeDeltaContext)
  const setTimeDelta = timeDeltaContext?.setTimeDelta
  const clearTimeDelta = timeDeltaContext?.clearTimeDelta
  const editModeContext = useContext(EditModeContext)
  const timeEntry = record.times[0]
  const originalTime = timeEntry.time
  const originalMinutes = Math.abs(originalTime)
  const originalSign = originalTime < 0 ? -1 : 1

  const { hours, minutes, handleHoursChange, handleMinutesChange, currentMinutes } = useClampedTime(originalMinutes)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [lastSubmittedTime, setLastSubmittedTime] = useState(originalTime)

  const updateMutation = useUpdateResourcesRecordMutation()
  const jobTitle = `${record.jobnumber.order_number_title}. ${record.jobnumber.customer_title}`

  const timeDelta = useMemo(() => currentMinutes - originalTime, [currentMinutes, originalTime])
  const updatedTimes = useMemo(
    () =>
      record.times.map(time =>
        time.id === timeEntry.id ? { ...time, time: currentMinutes * originalSign } : time
      ),
    [record.times, timeEntry.id, currentMinutes, originalSign]
  )

  const isValidTime = currentMinutes > 0

  // Update optimistic delta when time changes
  useEffect(() => {
    if (setTimeDelta) {
      setTimeDelta(record.id, timeDelta)
    }
    return () => {
      if (clearTimeDelta) {
        clearTimeDelta(record.id)
      }
    }
  }, [timeDelta, record.id, setTimeDelta, clearTimeDelta])

  useEffect(() => {
    const nextTime = currentMinutes * originalSign
    if (nextTime === lastSubmittedTime) {
      return
    }

    // Skip submission if time is invalid (0 hours and 0 minutes)
    if (!isValidTime) {
      return
    }

    const timer = window.setTimeout(async () => {
      setIsSubmitting(true)
      editModeContext?.startSaving()

      const payload = {
        resources_record: {
          jobnumber_id: record.jobnumber.id,
          times_attributes: [
            {
              id: timeEntry.id,
              time: nextTime, // Preserve original sign
              worktype_id: timeEntry.worktype_id,
              comment: timeEntry.comment,
            },
          ],
        },
      }

      try {
        await updateMutation.mutateAsync({ id: record.id, payload })
        setLastSubmittedTime(nextTime)
        onSave()
      } catch (error) {
        console.error('Failed to update record:', error)
      } finally {
        setIsSubmitting(false)
        editModeContext?.finishSaving()
      }
    }, 300)

    return () => window.clearTimeout(timer)
  }, [
    currentMinutes,
    originalSign,
    lastSubmittedTime,
    isValidTime,
    record.id,
    record.jobnumber.id,
    timeEntry.id,
    timeEntry.worktype_id,
    timeEntry.comment,
    updateMutation,
    onSave,
    editModeContext,
  ])

  return (
    <form onSubmit={e => e.preventDefault()}>
      <div className="row">
        <div className="col-3 mb-3 mb-md-0">
          <div className="d-flex flex-column flex-md-row align-items-start">
            <div className="form-group mb-2 mb-md-0 w-100">
              <label className="mb-1 d-md-none" htmlFor="inline-edit-hours">Timer:</label>
              <div className="d-flex align-items-center">
                <input
                  id="inline-edit-hours"
                  type="number"
                  min="0"
                  max="23"
                  value={hours}
                  onChange={handleHoursChange}
                  className={`form-control text-center ${!isValidTime ? 'is-invalid' : ''}`}
                />
                <span className="mx-1 d-none d-md-inline">:</span>
              </div>
            </div>
            <div className="form-group mb-0 w-100">
              <label className="mb-1 d-md-none" htmlFor="inline-edit-minutes">Minutter:</label>
              <input
                id="inline-edit-minutes"
                type="number"
                min="0"
                max="59"
                value={minutes}
                onChange={handleMinutesChange}
                className={`form-control text-center ${!isValidTime ? 'is-invalid' : ''}`}
              />
            </div>
          </div>
        </div>
        <div className="col-9 time-content">
          <div className="row time-title">
            <div className="col-12">
              {jobTitle}
            </div>
          </div>
          <div className="row mt-2">
          </div>
        </div>
      </div>
    </form>
  )
}

function formatTimeRecord(time: { time: number; worktype?: { category?: string; code?: string; title?: string }; comment?: string }): string {
  const formatted = minutesToTime(time.time)
  const category = time.worktype?.category
  let decorated = formatted

  if (category === 'overtime') {
    decorated = `(${formatted})`
  } else if (category === 'supplement') {
    decorated = `[${formatted}]`
  }

  let result = `${decorated} | ${time.worktype?.code} - ${time.worktype?.title}`
  if (time.comment) {
    result += ` | ${time.comment}`
  }

  return result
}

